'use client';

import { BundleCardCta } from '@components/templates/employer/job-bundles/cards/BundleCardCta';
import { CardRecommendedBadge } from '@components/templates/employer/job-bundles/cards/CardRecommendedBadge';
import { Input, Label } from '@components/common/Forms';
import { useState } from 'react';
import { Trans } from 'next-i18next';
import { BundleBenefits } from './BundleBenefits';

// Interface
// ******************************************
interface BundleCardProps {
  bundleData?: ApiSchema<'OfferBundle'>,
  iconColor?: string,
  isRecommended?: boolean,
  configurableBundleCombination?: ApiSchema<'OfferBundle'>,
}

/**
 * @description Renders a job bundle card. Renders nothing if there is no bundle data.
 * - **bundleData** - *{object}* - the bundle data to render; default undefined
 * - **className** - *{string}* - extra classes for the card; default empty string
 * - **anchorStyling** - *{string}* - the styling for the CTA button; default solid
 * - **anchorColor** - *{string}* - the color for the CTA button; default ink
 * - **iconColor** - *{string}* - the color for the icons; default text-secondary
 *
 * To preselect the desired bundle on the job activation page, we use the selectedBundleId search parameter.
 * like this: *selectedBundleId: bundleData?.packageId*
 */
export const BundleCard = (props: BundleCardProps) => {
  // Props destructuring
  const {
    bundleData,
    iconColor,
    isRecommended,
    configurableBundleCombination,
  } = props;

  const [isConfigurableBundle, setIsConfigurableBundle] = useState<boolean>(false);
  const selectedBundle = isConfigurableBundle ? configurableBundleCombination : bundleData;
  const isDiscounted = selectedBundle?.fullPrice !== selectedBundle?.price;

  // Render component
  // ******************************************
  return (
    <div className={`${isRecommended ? 'border border-dashed border-secondary' : 'border '} relative flex h-full flex-col rounded-md bg-surface p-5`}>
      {isRecommended && <CardRecommendedBadge />}
      <div className="min-h-10 leading-snug">
        {/* Bundle title  */}
        <h5 className="text-lg font-bold">
          {bundleData?.title}
        </h5>
      </div>


      <div className="mt-3 flex-1">
        <BundleBenefits
          benefits={selectedBundle?.benefits}
          iconColor={iconColor}
        />
      </div>

      <BundleCardCta
        bundleData={selectedBundle}
      />

      {configurableBundleCombination && (
        <Label
          className="flex items-start gap-2 text-sm text-ink-medium"
          htmlFor="creditAmount"
        >
          <Input
            className="asOptional mt-0.5"
            type="checkbox"
            onChange={() => setIsConfigurableBundle((prev) => !prev)}
          />

          <Trans
            i18nKey="services.page.offer.prohire.package.info.label"
            values={{
              amount: isDiscounted
                ? Math.ceil((configurableBundleCombination.price ?? 0) - (bundleData?.price ?? 0))
                : Math.ceil((configurableBundleCombination.fullPrice ?? 0) - (bundleData?.fullPrice ?? 0)),
            }}
          />
        </Label>
      )}

    </div>
  );
};
